// SVG topology renderer (Packet Tracer / EVE-NG style).
//
// layout = {
//   width, height,
//   nodes: { id: { x, y, type: 'pc'|'server'|'switch'|'router'|'cloud', sub, dim } },
//   zones: [{ x, y, w, h, label, cloud }],
//   linkLabels: { 'DEV:Ifc': 'extra text under the interface label' },
//   notes: [{ x, y, text, cls }],
//   hideLinks: ['linkId'],
//   overlays: [{ kind:'vpn', from, to, dev, ifc, label } | { kind:'qos', from, to, label }],
// }
// opts = { linkLights, onNode(id), onLink(link), selectPorts:{selected:Set, onToggle}, highlight:{links:Set, nodes:Set}, flows:[{from,to,label}] }

import { svg } from './h.js';
import { linkState, dev, ifStatus } from '../engine/net/sim.js';
import { shortIf } from '../engine/ios/ifnames.js';

const ICON = {
  router(g) {
    g.append(
      svg('ellipse', { cx: 0, cy: 8, rx: 30, ry: 11, class: 'ic-body' }),
      svg('rect', { x: -30, y: -8, width: 60, height: 16, class: 'ic-body', stroke: 'none' }),
      svg('line', { x1: -30, y1: -8, x2: -30, y2: 8, class: 'ic-edge' }),
      svg('line', { x1: 30, y1: -8, x2: 30, y2: 8, class: 'ic-edge' }),
      svg('ellipse', { cx: 0, cy: -8, rx: 30, ry: 11, class: 'ic-top' }),
      // four arrows (Cisco router glyph)
      svg('path', { d: 'M-17,-13 L-7,-9 M-7,-9 l-4,-1 m4,1 l-2,3  M17,-3 L7,-7 M7,-7 l4,1 m-4,-1 l2,-3  M-5,-17 L3,-15 M3,-15 l-3,-2 m3,2 l-3,2  M5,1 L-3,-1 M-3,-1 l3,2 m-3,-2 l3,-2', class: 'ic-glyph' }),
    );
  },
  switch(g) {
    g.append(
      svg('path', { d: 'M-34,-6 L-22,-16 L34,-16 L22,-6 Z', class: 'ic-top' }),
      svg('path', { d: 'M-34,-6 L22,-6 L22,14 L-34,14 Z', class: 'ic-body' }),
      svg('path', { d: 'M22,-6 L34,-16 L34,4 L22,14 Z', class: 'ic-side' }),
      svg('path', { d: 'M-20,-9 L0,-9 m-4,-2 l4,2 l-4,2  M8,-13 L-12,-13 m4,-2 l-4,2 l4,2', class: 'ic-glyph' }),
      ...[-26, -18, -10, -2, 6, 14].map((x) => svg('rect', { x, y: 3, width: 5, height: 4, class: 'ic-port' })),
    );
  },
  pc(g) {
    g.append(
      svg('rect', { x: -22, y: -20, width: 44, height: 30, rx: 3, class: 'ic-body' }),
      svg('rect', { x: -18, y: -16, width: 36, height: 22, class: 'ic-screen' }),
      svg('path', { d: 'M-6,10 L-8,17 L8,17 L6,10', class: 'ic-body' }),
      svg('rect', { x: -14, y: 17, width: 28, height: 3, rx: 1, class: 'ic-body' }),
    );
  },
  server(g) {
    g.append(
      svg('rect', { x: -16, y: -24, width: 32, height: 46, rx: 3, class: 'ic-body' }),
      ...[-18, -9, 0].map((y) => svg('rect', { x: -11, y, width: 22, height: 5, rx: 1, class: 'ic-screen' })),
      svg('circle', { cx: 8, cy: 14, r: 2, class: 'ic-led' }),
    );
  },
  cloud(g) {
    g.append(svg('path', { d: 'M-40,12 C-54,12 -54,-8 -38,-8 C-38,-24 -14,-26 -8,-14 C-2,-28 26,-26 26,-10 C44,-12 46,12 30,12 Z', class: 'ic-body' }));
  },
};

/** Point along segment a→b at fraction t, offset perpendicular by `off`. */
function along(a, b, t, off = 0) {
  const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
  return { x: a.x + dx * t + (-dy / len) * off, y: a.y + dy * t + (dx / len) * off };
}

export function renderTopology(layout, net, opts = {}) {
  const W = layout.width, H = layout.height;
  const root = svg('svg', { viewBox: `0 0 ${W} ${H}`, class: 'topo', role: 'img', 'aria-label': layout.title || 'Network topology diagram' });
  root.append(svg('defs', {},
    svg('pattern', { id: 'grid', width: 20, height: 20, patternUnits: 'userSpaceOnUse' }, svg('path', { d: 'M20 0H0V20', class: 'grid-line' })),
    svg('marker', { id: 'arrow', viewBox: '0 0 10 10', refX: 9, refY: 5, markerWidth: 6, markerHeight: 6, orient: 'auto-start-reverse' }, svg('path', { d: 'M0,0 L10,5 L0,10 z', class: 'flow-head' })),
  ));
  root.append(svg('rect', { x: 0, y: 0, width: W, height: H, fill: 'url(#grid)' }));

  for (const z of layout.zones || []) {
    root.append(svg('rect', { x: z.x, y: z.y, width: z.w, height: z.h, rx: 10, class: `zone ${z.cloud ? 'zone-cloud' : ''} ${z.cls || ''}` }));
    root.append(svg('text', { x: z.x + 12, y: z.y + 18, class: 'zone-label' }, z.label));
  }

  const pos = (id) => layout.nodes[id];
  const hl = opts.highlight || {};
  // ---- links
  // Parallel links (EtherChannel members) are drawn side by side, not on top of each other.
  const pairKey = (l) => [l.a.dev, l.b.dev].sort().join('~');
  const pairs = {};
  for (const l of net.links) (pairs[pairKey(l)] = pairs[pairKey(l)] || []).push(l.id);
  for (const l of net.links) {
    if (layout.hideLinks?.includes(l.id)) continue;
    const A0 = pos(l.a.dev), B0 = pos(l.b.dev);
    if (!A0 || !B0) continue;
    const grp = pairs[pairKey(l)];
    const shift = (grp.indexOf(l.id) - (grp.length - 1) / 2) * 18 * (l.a.dev < l.b.dev ? 1 : -1);
    const A = shift ? along(A0, B0, 0, shift) : A0;
    const B = shift ? along(B0, A0, 0, -shift) : B0;
    const st = linkState(net, l);
    const g = svg('g', { class: `link ${opts.onLink ? 'clickable' : ''} ${hl.links?.has(l.id) ? 'hl' : ''}`, 'data-link': l.id });
    const fiber = /fiber/i.test(l.cable.type);
    g.append(svg('line', { x1: A.x, y1: A.y, x2: B.x, y2: B.y, class: `wire ${fiber ? 'fiber' : ''} ${l.cable.seated ? '' : 'unseated'}` }));
    if (opts.onLink) {
      g.append(svg('line', { x1: A.x, y1: A.y, x2: B.x, y2: B.y, class: 'wire-hit', onclick: () => opts.onLink(l) }));
    }
    for (const [end, P, Q] of [[l.a, A, B], [l.b, B, A]]) {
      const d = Math.hypot(Q.x - P.x, Q.y - P.y);
      const t = Math.min(0.42, 46 / d);
      const lab = along(P, Q, t + 0.02, -12);
      const key = `${end.dev}:${end.ifc}`;
      g.append(svg('text', { x: lab.x, y: lab.y, class: 'if-label', 'text-anchor': 'middle' }, shortIf(end.ifc).replace('Gi', 'G')));
      if (layout.linkLabels?.[key]) { const sl = along(P, Q, t + 0.02, -24); g.append(svg('text', { x: sl.x, y: sl.y, class: 'if-sub', 'text-anchor': 'middle' }, layout.linkLabels[key])); }
      if (opts.linkLights) {
        const led = along(P, Q, Math.min(0.3, 34 / d));
        const cls = st === 'up' ? 'led-up' : st === 'errors' ? 'led-err' : 'led-down';
        g.append(svg('circle', { cx: led.x, cy: led.y, r: 4.2, class: `led ${cls}` }));
      }
      // Port selection (e.g. "which ports will this broadcast leave from?")
      const sp = opts.selectPorts;
      if (sp && dev(net, end.dev).kind === 'switch') {
        const p = along(P, Q, Math.min(0.45, 70 / d));
        const on = sp.selected.has(key);
        g.append(svg('circle', { cx: p.x, cy: p.y, r: 9, class: `port-pick ${on ? 'on' : ''} ${sp.result?.[key] || ''}`, 'data-port': key, tabindex: 0, onclick: () => sp.onToggle(key) }));
      }
    }
    if (layout.linkNotes?.[l.id]) {
      const m = along(A, B, 0.5, 14);
      g.append(svg('text', { x: m.x, y: m.y, class: 'link-note', 'text-anchor': 'middle' }, layout.linkNotes[l.id]));
    }
    root.append(g);
  }

  // ---- logical overlays: VPN tunnels (state from the tunnel interface) and QoS direction
  for (const o of layout.overlays || []) {
    const A = pos(o.from), B = pos(o.to);
    if (!A || !B) continue;
    if (o.kind === 'vpn') {
      const up = o.dev && net.devices[o.dev]?.interfaces?.[o.ifc] ? ifStatus(net, o.dev, o.ifc).protocol === 'up' : false;
      const m = along(A, B, 0.5, o.bend ?? -70);
      const g = svg('g', { class: `vpn-tunnel ${up ? 'up' : 'down'}`, 'data-tunnel': `${o.dev}:${o.ifc}` });
      g.append(svg('path', { d: `M${A.x},${A.y} Q${m.x},${m.y} ${B.x},${B.y}`, class: 'vpn-path' }));
      const t = along(A, B, 0.5, (o.bend ?? -70) / 2 - 10);
      g.append(svg('text', { x: t.x, y: t.y, class: 'vpn-label', 'text-anchor': 'middle' }, `🔒 ${o.label || 'IPsec tunnel'} · ${up ? 'UP' : 'DOWN'}`));
      root.append(g);
    } else if (o.kind === 'qos') {
      const a = along(A, B, 0.12, o.off ?? 22), b = along(A, B, 0.42, o.off ?? 22);
      root.append(svg('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: 'qos-arrow', 'marker-end': 'url(#arrow)' }));
      const t = along(A, B, 0.27, (o.off ?? 22) + 14);
      root.append(svg('text', { x: t.x, y: t.y, class: 'qos-label', 'text-anchor': 'middle' }, o.label || 'QoS output'));
    }
  }

  // ---- traffic flows (arrows)
  for (const f of opts.flows || []) {
    const A = pos(f.from), B = pos(f.to);
    const a = along(A, B, 0.18, f.off ?? -26), b = along(A, B, 0.82, f.off ?? -26);
    root.append(svg('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: `tflow ${f.cls || ''}`, 'marker-end': 'url(#arrow)' }));
    if (f.label) { const m = along(A, B, 0.5, (f.off ?? -26) - 8); root.append(svg('text', { x: m.x, y: m.y, class: 'tflow-label', 'text-anchor': 'middle' }, f.label)); }
  }

  // ---- nodes
  for (const [id, n] of Object.entries(layout.nodes)) {
    const d = net.devices[id];
    const clickable = opts.onNode && opts.consoleDevices?.includes(id);
    const g = svg('g', {
      class: `node node-${n.type} ${n.dim ? 'dim' : ''} ${clickable ? 'clickable' : ''} ${opts.active === id ? 'active' : ''} ${hl.nodes?.has(id) ? 'hl' : ''}`,
      transform: `translate(${n.x},${n.y})`, 'data-node': id,
      ...(clickable ? { tabindex: 0, role: 'button', 'aria-label': `Open console on ${id}`, onclick: () => opts.onNode(id), onkeydown: (e) => { if (e.key === 'Enter') opts.onNode(id); } } : {}),
    });
    g.append(svg('circle', { r: 40, class: 'node-halo' }));
    ICON[n.type](g);
    g.append(svg('text', { y: n.type === 'server' ? 40 : 36, class: 'node-name', 'text-anchor': 'middle' }, d?.hostname || n.label || id));
    if (n.sub) g.append(svg('text', { y: n.type === 'server' ? 53 : 49, class: 'node-sub', 'text-anchor': 'middle' }, n.sub));
    if (n.badge) g.append(svg('text', { y: -34, class: 'node-badge', 'text-anchor': 'middle' }, n.badge));
    root.append(g);
  }
  for (const nt of layout.notes || []) root.append(svg('text', { x: nt.x, y: nt.y, class: `note ${nt.cls || ''}`, 'text-anchor': nt.anchor || 'start' }, nt.text));
  return root;
}
